import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import FileUploader from 'apollo-react/components/FileUploader';
import LinearProgress from 'apollo-react/components/LinearProgress';
import FieldWrapper from '../../fields/FieldWrapper';
import HelperText from '../../fields/HelperText';
import FileList from './FileList';
import styles from '../IDSField.module.css';

/**
 * FileFieldInput - File upload component with eager upload support
 *
 * ARCHITECTURE:
 * This component handles file attachments using the centralized file module.
 * File types:
 * 1. Server files (existingFiles) - Previously submitted files (status: AVAILABLE)
 * 2. Staged files - Eagerly uploaded files waiting for submission (status: STAGED)
 * 3. Local files (value) - File objects not yet uploaded (legacy non-eager path)
 *
 * All files now use the same ID space (files.id = sub_form_files.id after migration).
 *
 * FILE DISPLAY:
 * - Uses custom FileList component for server and staged files (supports strikethrough for deleted files)
 * - Uses Apollo FileUpload only for upload functionality (no file display)
 * - Staged files take priority over server files (draft updates replace originals) when loading a draft.
 *
 * PENDING DELETION FLOW (for send-back edit mode):
 * 1. User clicks delete icon on a file
 * 2. File is marked as "pending deletion" (shown with strikethrough)
 * 3. Delete button becomes disabled for that file
 * 4. On confirm (✓): Validation runs, if valid, deletions are processed
 * 5. On cancel (✗): Pending deletions are cleared, files restored
 * -
 * For new requests, no pending delation for both request and draft.
 *
 * FILE LIMIT: Default 1 file per field (forward compatible for more)
 *
 * @param {string} id - Field ID
 * @param {string} label - Field label
 * @param {Array} value - Current file values (File objects, legacy path)
 * @param {Function} onUpload - Legacy upload handler (when eagerUploadEnabled=false)
 * @param {Function} onDelete - Legacy delete handler
 * @param {Function} onServerFileDelete - Called when server file deletion is CONFIRMED
 * @param {boolean} disabled - Whether field is disabled
 * @param {boolean} required - Whether field is required
 * @param {Object} columnStyle - Column width styling
 * @param {string} helperText - Helper text shown below field
 * @param {boolean} showError - Whether to show error state
 * @param {Object} fileConfig - File configuration { accept, maxItems, maxSize }
 * @param {Array} existingFiles - Server files from sub_form_files table
 * @param {number} formId - Form ID for scoping
 * @param {number} subformId - Subform ID for scoping
 * @param {Function} onStagedFileUpload - Eager upload handler
 * @param {Function} onStagedFileDelete - Staged file delete handler
 * @param {boolean} eagerUploadEnabled - Enable eager upload mode
 * @param {Array} initialStagedFiles - Pre-loaded staged files from draft
 * @param {boolean} enablePendingDelete - Enable pending delete flow (confirm/cancel)
 */
const FileFieldInput = forwardRef(
  (
    {
      id,
      label,
      value,
      onUpload,
      onDelete,
      onServerFileDelete,
      disabled,
      required,
      columnStyle,
      helperText,
      showError,
      fileConfig,
      existingFiles = [],
      formId,
      subformId,
      onStagedFileUpload,
      onStagedFileDelete,
      eagerUploadEnabled = false,
      initialStagedFiles = [],
      enablePendingDelete = false,
    },
    ref
  ) => {
    const { t } = useTranslation();

    // ============================================================
    // STATE MANAGEMENT
    // ============================================================

    /**
     * Server file IDs that are PENDING deletion (shown with strikethrough).
     * These are NOT yet confirmed - cleared on cancel, processed on confirm.
     */
    const [pendingServerDeletions, setPendingServerDeletions] = useState(
      new Set()
    );

    /**
     * Staged file IDs that are PENDING deletion (shown with strikethrough).
     */
    const [pendingStagedDeletions, setPendingStagedDeletions] = useState(
      new Set()
    );

    /**
     * Server file IDs that have been CONFIRMED for deletion.
     * These are filtered out from display immediately.
     */
    const [confirmedServerDeletions, setConfirmedServerDeletions] = useState(
      new Set()
    );

    /**
     * Staged files associated with this field.
     * Initialized from draft (initialStagedFiles) and updated on new uploads.
     */
    const [stagedFiles, setStagedFiles] = useState(initialStagedFiles);

    /**
     * Track files uploaded during current edit session (for cancel/rollback).
     * When user cancels editing, these files are deleted from the server.
     * When user confirms, this is cleared (uploads become permanent).
     */
    const [pendingUploads, setPendingUploads] = useState([]);

    /**
     * Snapshot of staged files at the start of edit session.
     * Used to restore state on cancel. This captures the TRUE original state
     * before any uploads during this edit session, avoiding the issue where
     * initialStagedFiles prop gets updated during the session.
     *
     * Using useRef to avoid the snapshot being part of dependency arrays
     * and to ensure the snapshot is captured synchronously.
     */
    const originalStagedFilesSnapshotRef = useRef(null);

    /** Upload in progress indicator */
    const [uploading, setUploading] = useState(false);

    /** Upload progress percentage (0-100) */
    const [uploadProgress, setUploadProgress] = useState(0);

    /**
     * Maximum files allowed.
     * Priority: fileConfig.maxItems > 1 (default for single file fields)
     * Note: For 'attachment' type fields, the default should be 10 (set in fileConfig by parent).
     * A value of 0 or null/undefined means use default of 1.
     */
    const maxItems = fileConfig?.maxItems > 0 ? fileConfig.maxItems : 1;

    // ============================================================
    // EFFECTS
    // ============================================================

    // Sync staged files when initialStagedFiles changes (draft load)
    useEffect(() => {
      if (initialStagedFiles.length > 0) {
        setStagedFiles(initialStagedFiles);
      }
    }, [initialStagedFiles]);

    // Reset deletion state when switching to a different field instance
    useEffect(() => {
      setPendingServerDeletions(new Set());
      setConfirmedServerDeletions(new Set());
    }, [id, subformId, formId]);

    /**
     * Server files to display (excluding confirmed deletions).
     * Server files are shown alongside staged files (append behavior, not replace).
     */
    const displayServerFiles = useMemo(() => {
      if (!existingFiles || existingFiles.length === 0) return [];

      return existingFiles
        .filter((file) => !confirmedServerDeletions.has(file.id))
        .map((file) => ({
          id: file.id,
          serverId: file.id,
          name: file.name || file.filename,
          size: parseInt(file.size, 10) || 0,
          mimeType: file.mimeType,
          isServerFile: true,
        }));
    }, [existingFiles, confirmedServerDeletions]);

    /**
     * Staged files to display (excluding pending deletions).
     * Note: Staged files pending deletion still show (with strikethrough).
     */
    const displayStagedFiles = useMemo(() => {
      return stagedFiles.map((file) => ({
        id: file.id || file.stagedId,
        stagedId: file.stagedId || file.id,
        name: file.name,
        size: parseInt(file.size, 10) || 0,
        mimeType: file.type || file.mimeType,
        isStagedFile: true,
      }));
    }, [stagedFiles]);

    /**
     * All files to display (server + staged + local).
     * Staged files take priority - if present, server files are hidden.
     */
    const allDisplayFiles = useMemo(() => {
      // Only include actual File objects as local files. Staged-file metadata
      // objects that land in `value` (via onUpload after eager upload) are
      // already covered by displayStagedFiles and must not be shown twice.
      const localFiles = (Array.isArray(value) ? value : [])
        .filter((f) => f instanceof File)
        .map((f) => ({
          id: f.name,
          name: f.name,
          size: f.size || 0,
          mimeType: f.type,
          isLocalFile: true,
          file: f,
        }));

      return [...displayStagedFiles, ...localFiles, ...displayServerFiles];
    }, [displayServerFiles, displayStagedFiles, value]);

    /**
     * Combined set of pending deletion IDs (for FileList component).
     */
    const allPendingDeletions = useMemo(() => {
      const combined = new Set();
      pendingServerDeletions.forEach((id) => combined.add(id));
      pendingStagedDeletions.forEach((id) => combined.add(id));
      return combined;
    }, [pendingServerDeletions, pendingStagedDeletions]);

    /**
     * Count of "active" files (not pending deletion) for limit check.
     */
    const activeFileCount = useMemo(() => {
      return allDisplayFiles.filter((f) => {
        const fileId = f.serverId || f.stagedId || f.id;
        return !allPendingDeletions.has(fileId);
      }).length;
    }, [allDisplayFiles, allPendingDeletions]);

    /** Whether upload limit is reached */
    const isLimitReached = activeFileCount >= maxItems;

    // ============================================================
    // IMPERATIVE HANDLE (for parent FieldEditor)
    // ============================================================

    useImperativeHandle(
      ref,
      () => ({
        /**
         * Process pending deletions - called when confirm (✓) is clicked.
         * Moves pending deletions to confirmed, notifies parent.
         * Also clears pending uploads (they become permanent).
         */
        confirmPendingDeletions: async () => {
          // Process pending server file deletions (bulk - single API call)
          const serverIds = Array.from(pendingServerDeletions);
          if (serverIds.length > 0) {
            setConfirmedServerDeletions(
              (prev) => new Set([...prev, ...serverIds])
            );
            if (onServerFileDelete) {
              // Send all file IDs at once for bulk deletion
              await onServerFileDelete(serverIds);
            }
          }
          setPendingServerDeletions(new Set());

          // Process pending staged file deletions
          const stagedIds = new Set(pendingStagedDeletions);
          if (stagedIds.size > 0) {
            // Remove from local state FIRST to prevent re-fetch of deleted files
            setStagedFiles((prev) =>
              prev.filter(
                (f) => !stagedIds.has(f.id) && !stagedIds.has(f.stagedId)
              )
            );
            // Then delete from server
            if (onStagedFileDelete) {
              await Promise.all(
                Array.from(stagedIds).map((stagedId) =>
                  onStagedFileDelete(stagedId)
                )
              );
            }
          }
          setPendingStagedDeletions(new Set());

          // Clear pending uploads - they are now permanent
          setPendingUploads([]);
          // Clear the snapshot since changes are confirmed
          originalStagedFilesSnapshotRef.current = null;
        },

        /**
         * Clear pending deletions - called when cancel (✗) is clicked.
         * Restores files to their original state.
         */
        cancelPendingDeletions: () => {
          setPendingServerDeletions(new Set());
          setPendingStagedDeletions(new Set());
        },

        /**
         * Cancel pending uploads - called when cancel (✗) is clicked.
         * Deletes files uploaded during this edit session from the server
         * and resets staged files to original state.
         */
        cancelPendingUploads: async () => {
          // Delete each pending upload from server
          if (pendingUploads.length > 0 && onStagedFileDelete) {
            await Promise.all(
              pendingUploads.map(async (fileId) => {
                try {
                  await onStagedFileDelete(fileId);
                } catch (e) {
                  console.warn('Failed to delete pending upload:', fileId, e);
                }
              })
            );
          }

          // Reset staged files to the snapshot taken at start of edit session
          // Use snapshot if available (captures true original state), otherwise fall back to prop
          const restoreState =
            originalStagedFilesSnapshotRef.current !== null
              ? originalStagedFilesSnapshotRef.current
              : initialStagedFiles || [];
          setStagedFiles(restoreState);
          setPendingUploads([]);
          // Clear the snapshot
          originalStagedFilesSnapshotRef.current = null;
        },

        /**
         * Check if there are any pending deletions.
         */
        hasPendingDeletions: () =>
          pendingServerDeletions.size > 0 || pendingStagedDeletions.size > 0,

        /**
         * Check if there are any pending uploads.
         */
        hasPendingUploads: () => pendingUploads.length > 0,

        /**
         * Check if field has any files (for validation).
         * Excludes files that are pending deletion.
         */
        hasFiles: () => {
          return activeFileCount > 0;
        },
      }),
      [
        pendingServerDeletions,
        pendingStagedDeletions,
        pendingUploads,
        activeFileCount,
        onServerFileDelete,
        onStagedFileDelete,
        initialStagedFiles,
      ]
    );

    // ============================================================
    // HANDLERS
    // ============================================================

    /**
     * Handle file deletion.
     * - If enablePendingDelete=true: mark pending (confirm/cancel flow)
     * - Otherwise: delete immediately and update UI
     */
    const handleFileDelete = useCallback(
      async (fileToDelete) => {
        const fileId =
          fileToDelete.serverId || fileToDelete.stagedId || fileToDelete.id;

        // Show confirmation dialog
        const confirmed = window.confirm(
          t('fileUpload.confirmDelete', {
            fileName: fileToDelete.name,
            defaultValue: `Delete "${fileToDelete.name}"? This will remove the file.`,
          })
        );

        if (!confirmed) return;

        if (enablePendingDelete) {
          if (fileToDelete.isServerFile) {
            // Mark server file as pending deletion
            setPendingServerDeletions((prev) => new Set([...prev, fileId]));
          } else if (fileToDelete.isStagedFile) {
            // Mark staged file as pending deletion
            setPendingStagedDeletions((prev) => new Set([...prev, fileId]));
          } else if (fileToDelete.isLocalFile && onDelete) {
            // Local files (legacy) - delete immediately
            onDelete(fileToDelete.file || fileToDelete);
          }
          return;
        }

        // Immediate deletion flow (no confirm/cancel)
        if (fileToDelete.isServerFile) {
          setConfirmedServerDeletions((prev) => new Set([...prev, fileId]));
          if (onServerFileDelete) {
            // Send as array for bulk-compatible API
            await onServerFileDelete([fileId]);
          }
        } else if (fileToDelete.isStagedFile) {
          // Remove from local state FIRST to prevent re-fetch of deleted files
          setStagedFiles((prev) =>
            prev.filter((f) => f.id !== fileId && f.stagedId !== fileId)
          );
          if (onStagedFileDelete) {
            await onStagedFileDelete(fileId);
          }
        } else if (fileToDelete.isLocalFile && onDelete) {
          onDelete(fileToDelete.file || fileToDelete);
        }
      },
      [enablePendingDelete, t, onDelete, onServerFileDelete, onStagedFileDelete]
    );

    /**
     * Handle eager file upload.
     */
    const handleEagerUpload = useCallback(
      async (files) => {
        if (!eagerUploadEnabled || !onStagedFileUpload || !subformId) {
          if (onUpload) {
            onUpload(files);
          }
          return;
        }

        const filesToUpload = Array.isArray(files) ? files : [files];
        const remainingSlots = maxItems - activeFileCount;
        const filesToProcess = filesToUpload.slice(0, remainingSlots);

        const uploadedFiles = [];

        for (const file of filesToProcess) {
          setUploading(true);
          setUploadProgress(0);

          try {
            // eslint-disable-next-line no-await-in-loop
            const stagedFile = await onStagedFileUpload(file, {
              formId,
              subformId,
              fieldId: id,
              maxFiles: maxItems,
              onProgress: setUploadProgress,
            });

            if (stagedFile) {
              setStagedFiles((prev) => {
                if (
                  originalStagedFilesSnapshotRef.current === null &&
                  enablePendingDelete
                ) {
                  originalStagedFilesSnapshotRef.current = [...prev];
                }
                return [...prev, stagedFile];
              });
              const uploadedId = stagedFile.id || stagedFile.stagedId;
              if (uploadedId) {
                setPendingUploads((prev) => [...prev, uploadedId]);
              }
              uploadedFiles.push(stagedFile);
            }
          } catch (error) {
            console.error('Error uploading staged file:', error);
          } finally {
            setUploading(false);
            setUploadProgress(0);
          }
        }

        // Propagate ALL uploaded files at once to avoid stale closure in
        // useFieldHandlers.handleFileUpload, which reads `value` from its
        // closure to build the combined array. Calling per-file would cause
        // each call to see the same stale `value`, overwriting previous files.
        if (onUpload && uploadedFiles.length > 0) {
          onUpload(uploadedFiles);
        }
      },
      [
        eagerUploadEnabled,
        onStagedFileUpload,
        onUpload,
        formId,
        subformId,
        id,
        maxItems,
        activeFileCount,
        enablePendingDelete,
      ]
    );

    /**
     * Handle file upload (entry point).
     */
    const handleUpload = useCallback(
      async (files) => {
        if (isLimitReached) return;

        if (eagerUploadEnabled) {
          await handleEagerUpload(files);
        } else if (onUpload) {
          const filesToUpload = Array.isArray(files) ? files : [files];
          const remainingSlots = maxItems - activeFileCount;
          const filesToProcess = filesToUpload.slice(0, remainingSlots);
          if (filesToProcess.length > 0) {
            onUpload(filesToProcess);
          }
        }
      },
      [
        isLimitReached,
        eagerUploadEnabled,
        handleEagerUpload,
        onUpload,
        maxItems,
        activeFileCount,
      ]
    );

    // Expose getStagedFileIds for parent access (DOM-based)
    const getStagedFileIds = useCallback(() => {
      return stagedFiles.map((f) => f.id || f.stagedId);
    }, [stagedFiles]);

    useEffect(() => {
      const element = document.querySelector(`[data-file-field-id="${id}"]`);
      if (element) {
        element.getStagedFileIds = getStagedFileIds;
      }
    }, [id, getStagedFileIds]);

    // ============================================================
    // RENDER
    // ============================================================

    return (
      <FieldWrapper style={columnStyle}>
        <div className={styles.fileUploadContainer} data-file-field-id={id}>
          {/* Upload progress indicator */}
          {uploading && (
            <div className={styles.uploadProgressContainer}>
              <LinearProgress
                value={uploadProgress}
                variant="determinate"
                style={{ marginBottom: 8 }}
              />
              <span className={styles.uploadProgressText}>
                {t('common.uploading', 'Uploading...')} {uploadProgress}%
              </span>
            </div>
          )}

          {/* Custom file list display (server + staged files) */}
          <FileList
            files={allDisplayFiles}
            pendingDeletionIds={allPendingDeletions}
            disabled={disabled}
            onDelete={handleFileDelete}
          />

          {/* Apollo FileUpload for upload functionality only */}
          <FileUploader
            id={id}
            name={id}
            label={label}
            value={[]} // Don't use Apollo for file display
            onUpload={handleUpload}
            onFileDelete={() => {}} // Handled by FileList
            disabled={disabled || uploading || isLimitReached}
            required={required}
            accept={fileConfig?.accept}
            maxItems={maxItems}
            maxSize={fileConfig?.maxSize}
            multiple={maxItems > 1}
            fullWidth
          />

          {/* Limit reached indicator */}
          {isLimitReached && (
            <span className={styles.uploadLimitText}>
              {t('fileUpload.maxFilesReached', {
                count: maxItems,
                defaultValue:
                  maxItems === 1
                    ? 'Max uploaded files reached'
                    : 'Max uploaded files reached ({{count}})',
              })}
            </span>
          )}
        </div>
        <HelperText text={helperText} error={showError} />
      </FieldWrapper>
    );
  }
);

FileFieldInput.displayName = 'FileFieldInput';

export default FileFieldInput;
